<template>
  <v-autocomplete
    v-model="store.selected"
    v-model:search="search"
    :items="items"
    item-title="id"
    item-value="id"
    :custom-filter="filter"
    :loading="requests.Search.active"
    clearable
    hide-details
    :hide-no-data="requests.Search.active"
    menu-icon=""
    prepend-inner-icon="$search"
    density="comfortable"
    class="search mx-4"
    variant="solo-filled"
    @update:focused="focusedUpdated"
    @update:search="searchUpdated"
    @click:clear="clearClicked"
  >
    <template #item="{ props, item: { raw } }">
      <SearchItem
        v-bind="props"
        title=""
        :item="raw"
        :search="search"
        :prepend-icon="historyShown && history.length ? '$history' : null"
        @click="addToHistory(raw)"
      />
    </template>
  </v-autocomplete>
</template>

<script setup lang="ts">
import { searchJobs } from '@/api/search'
import SearchItem from '@/components/General/SearchItem.vue'
import { useRequestsStore } from '@/stores/requests'
import { useSearchStore } from '@/stores/search'
import type { JobSearchItem } from '@/types/JobSearch'
import { debounce } from 'lodash-es'
import { ref } from 'vue'

const store = useSearchStore()
const requests = useRequestsStore()

const minLength = 3
const search = ref<string>()
const previousSearch = ref<string>()
const items = ref<JobSearchItem[]>([])

const focused = ref(false)

const historyLimit = 5
const history = ref<JobSearchItem[]>([])
const historyShown = ref<boolean>(false)

function filter(value: string, query: string, item: { raw: any } | undefined) {
  const text = Object.values(item?.raw).join(' ')
  const searchTerms = query.split(' ')

  return searchTerms.every((term) =>
    text.toLowerCase().includes(term.toLowerCase())
  )
}

const dSearch = debounce(async (value?: string) => {
  if (!value || value.trim().length < minLength) {
    // v-autocomplete updates search to empty string on blur, so only update if focused
    if (focused.value) {
      previousSearch.value = value
    }
    historyShown.value = true
    return (items.value = history.value)
  }

  const unchanged = value.toLowerCase() === previousSearch.value?.toLowerCase()
  if (unchanged) {
    return
  }

  historyShown.value = false

  items.value = []
  items.value = await searchJobs(value)
  previousSearch.value = value
}, 500)

function focusedUpdated(value: boolean) {
  focused.value = value
}

function searchUpdated(value?: string) {
  dSearch(value)
}

function clearClicked() {
  previousSearch.value = ''
}

function addToHistory(item: JobSearchItem) {
  const exists = history.value.find((i) => i.id === item.id)
  if (exists) {
    return
  }

  history.value = [item, ...history.value]
  history.value.length = Math.min(history.value.length, historyLimit)
}

defineExpose({
  search,
  focused,
  focusedUpdated,
  clearClicked,
  items,
  previousSearch,
  history,
  filter,
  searchUpdated,
  addToHistory,
})
</script>

<style scoped>
:deep(.v-field) {
  box-shadow: none;
}
</style>
